import type {
  Group,
  GroupItem,
  GroupItemAlterCode,
  GroupItemCustomer,
  GroupItemMaterial,
  GroupItemsWithMaterials,
  GroupItemTraderSite,
  GroupItemWithAncestry,
  GroupTableFilterData,
} from '@/types/Groups';
import { axios } from './axiosClient';
import { validate } from '@/utils/errors/helpers';
import { getGroupsByCategorySchema } from '@/types/schemas/groups/getGroupsByCategory';
import { getGroupItemsByGroupSchema } from '@/types/schemas/groups/getGroupItemsByGroup';
import { getGroupItemMaterialsForGroupItemIdSchema } from '@/types/schemas/groups/getGroupItemMaterialsForGroupItemId';
import { getGroupItemsAlterCodeForGroupItemIdSchema } from '@/types/schemas/groups/getGroupItemsAlterCodeForGroupItemId';
import { getGroupItemsCustomerForGroupItemIdSchema } from '@/types/schemas/groups/getGroupItemsCustomerForGroupItemId';
import { getGroupItemsTraderSiteForGroupItemIdSchema } from '@/types/schemas/groups/getGroupItemsTraderSiteForGroupItemId';
import {
  getGroupItemMaterialsGroupItemsSchema,
  getGroupItemTraderSitesGroupItemsSchema,
} from '@/types/schemas/groups/groupItem';
import type { TableFilterData } from '@/types/Base';
import { getGroupItemsForQstGenericGroupsMaterialsSchema } from '@/types/schemas/groups/getGroupItemsForQstGenericGroupsMaterials';

export async function getGroupItemsWithTradeSites({
  page = 1,
  itemsPerPage = 0,
  query = '',
  sortColumn,
  sortAscending,
  excludeIDs,
  groupId,
}: GroupTableFilterData): Promise<{ data: GroupItemWithAncestry[]; count: number }> {
  const skip = (page - 1) * itemsPerPage;
  const response = await axios.post(
    `/v2/groups/group-items-tradersites/search?take=${itemsPerPage}&skip=${skip}`,
    {
      searchText: query,
      sortColumn,
      sortAscending,
      excludeIDs,
      groupId,
    }
  );
  return { data: response.data.data, count: response.data.count };
}

export async function getGroupItemsWithMaterials({
  page = 1,
  itemsPerPage = 0,
  query = '',
  sortColumn,
  sortAscending,
  excludeIDs,
  groupId,
}: GroupTableFilterData): Promise<{ data: GroupItemWithAncestry[]; count: number }> {
  const skip = (page - 1) * itemsPerPage;
  const response = await axios.post(
    `/v2/groups/group-items-materials/search?take=${itemsPerPage}&skip=${skip}`,
    {
      searchText: query,
      sortColumn,
      sortAscending,
      excludeIDs,
      groupId,
    }
  );
  return { data: response.data.data, count: response.data.count };
}

export async function getGroupsByCategory(category: string) {
  const response = await axios.get(`/v2/groups/${category}`);

  const valid = validate(getGroupsByCategorySchema, response.data.data);

  return { data: valid, count: response.data.count };
}

export async function getAllRootGroupItems() {
  const response = await axios.get('/v2/groups/root-group-items', {
    validateStatus: (status: number) => status == 404 || status == 200,
  });
  if (response.status === 404) return null;

  const groupItems = validate(getGroupItemsByGroupSchema, response.data.data);
  return groupItems;
}

export async function getGroupItemById(id: string): Promise<GroupItem | null> {
  const response = await axios.get(`/v2/groups/group-items/${id}`, {
    validateStatus: (status: number) => status == 404 || status == 200,
  });
  if (response.status === 404) return null;

  const groupItem = validate(getGroupItemsByGroupSchema, [response.data]);
  return groupItem[0];
}

export async function getGroupItemsByGroup(groupId: string) {
  const response = await axios.get(`/v2/groups/${groupId}/group-items`);
  const valid = validate(getGroupItemsByGroupSchema, response.data.data);

  return { data: valid, count: response.data.count };
}

export async function addOrUpdateGroupCollection(groups: Group[]) {
  const response = await axios.post('v2/groups', groups);
  if (response.status === 201) return true;
  return false;
}

export async function getGroupItemMaterialForGroupItemId({
  page = 1,
  itemsPerPage = 0,
  query = '',
  sortColumn,
  sortAscending,
  isActive,
  groupItemId,
}: TableFilterData & { groupItemId?: string }): Promise<{
  data: GroupItemMaterial[];
  count: number;
}> {
  const skip = (page - 1) * itemsPerPage;
  const response = await axios.post(
    `/v2/groups/group-items/material/${groupItemId}/search?take=${itemsPerPage}&skip=${skip}`,
    { searchText: query, sortColumn, sortAscending, isActive }
  );

  const valid = validate(getGroupItemMaterialsForGroupItemIdSchema, response.data.data);
  return { data: valid, count: response.data.count };
}

export async function getGroupItemsAlterCodeForGroupItemId({
  page = 1,
  itemsPerPage = 0,
  query = '',
  sortColumn,
  sortAscending,
  isActive,
  groupItemId,
}: TableFilterData & { groupItemId?: string }): Promise<{
  data: GroupItemAlterCode[];
  count: number;
}> {
  const skip = (page - 1) * itemsPerPage;
  const response = await axios.post(
    `/v2/groups/group-items/alterCode/${groupItemId}/search?take=${itemsPerPage}&skip=${skip}`,
    { searchText: query, sortColumn, sortAscending, isActive }
  );

  const valid = validate(getGroupItemsAlterCodeForGroupItemIdSchema, response.data.data);
  return { data: valid, count: response.data.count };
}

export async function getGroupItemsCustomerForGroupItemId({
  page = 1,
  itemsPerPage = 0,
  query = '',
  sortColumn,
  sortAscending,
  isActive,
  groupItemId,
}: TableFilterData & { groupItemId?: string }): Promise<{
  data: GroupItemCustomer[];
  count: number;
}> {
  const skip = (page - 1) * itemsPerPage;
  const response = await axios.post(
    `/v2/groups/group-items/customer/${groupItemId}/search?take=${itemsPerPage}&skip=${skip}`,
    { searchText: query, sortColumn, sortAscending, isActive }
  );

  const valid = validate(getGroupItemsCustomerForGroupItemIdSchema, response.data.data);
  return { data: valid, count: response.data.count };
}

export async function getGroupItemsTraderSiteForGroupItemId({
  page = 1,
  itemsPerPage = 0,
  query = '',
  sortColumn,
  sortAscending,
  isActive,
  groupItemId,
}: TableFilterData & { groupItemId?: string }): Promise<{
  data: GroupItemTraderSite[];
  count: number;
}> {
  const skip = (page - 1) * itemsPerPage;
  const response = await axios.post(
    `/v2/groups/group-items/tradersite/${groupItemId}/search?take=${itemsPerPage}&skip=${skip}`,
    { searchText: query, sortColumn, sortAscending, isActive }
  );

  const valid = validate(getGroupItemsTraderSiteForGroupItemIdSchema, response.data.data);
  return { data: valid, count: response.data.count };
}

export async function getGroupItemsForQstGenericGroups(groupId: string): Promise<{
  data: GroupItem[];
  count: number;
}> {
  const response = await axios.get(`/v2/groups/${groupId}/group-items/generic-groups`);
  const valid = validate(getGroupItemsByGroupSchema, response.data.data);

  return { data: valid, count: response.data.count };
}
export async function getGroupItemsForQstGenericGroupsMaterials(groupId: string): Promise<{
  data: GroupItemsWithMaterials[];
  count: number;
}> {
  const response = await axios.get(`/v2/groups/${groupId}/group-items/generic-groups-materials`);
  const valid = validate(getGroupItemsForQstGenericGroupsMaterialsSchema, response.data.data);

  return { data: valid, count: response.data.count };
}
export async function getGroupItemsMaterialsGroupItems(): Promise<{
  data: GroupItem[];
  count: number;
}> {
  const response = await axios.get(`/v2/groups/group-item-materials/group-items`);
  const valid = validate(getGroupItemMaterialsGroupItemsSchema, response.data.data);

  return { data: valid, count: response.data.count };
}

export async function getGroupItemsTraderSitesGroupItems(): Promise<{
  data: GroupItem[];
  count: number;
}> {
  const response = await axios.get(`/v2/groups/group-item-tradersites/group-items`);
  const valid = validate(getGroupItemTraderSitesGroupItemsSchema, response.data.data);

  return { data: valid, count: response.data.count };
}
